import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StrikerBrand } from '@striker/ui-shell';

type CatalogModule = {
  code: string;
  name: string;
  price_monthly: number;
  price_annual: number;
  is_sellable: boolean;
};

type CatalogPlan = {
  id: number;
  plan_code: string;
  name: string;
  price_monthly: number;
  currency_symbol: string;
  billing_period: string;
  is_free_plan?: boolean;
  modules: CatalogModule[];
};

type CatalogCategory = {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  module_count: number;
};

function formatPrice(value: number, symbol: string) {
  if (!Number.isFinite(value)) return '—';
  if (value <= 0) return 'Gratis';
  return `${value.toFixed(2).replace('.', ',')} ${symbol || '€'}`;
}

export function JoinPage() {
  const [plan, setPlan] = useState<CatalogPlan | null>(null);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [busy, setBusy] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Load categories on mount
  useEffect(() => {
    let cancelled = false;
    void fetch('/api/v1/auth/catalog/categories')
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as { categories?: CatalogCategory[] };
        if (!cancelled && res.ok && Array.isArray(body.categories)) {
          setCategories(body.categories);
          if (body.categories.length > 0 && !selectedCategory) {
            setSelectedCategory(body.categories[0].code);
          }
        }
      })
      .catch(() => {
        /* optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load plans when category changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      setLoadError(null);
      try {
        const url = selectedCategory
          ? `/api/v1/auth/catalog/plans?category=${encodeURIComponent(selectedCategory)}`
          : '/api/v1/auth/catalog/plans';
        const res = await fetch(url);
        const body = (await res.json().catch(() => ({}))) as {
          plans?: CatalogPlan[];
          error?: string;
          message?: string;
        };
        if (!res.ok) throw new Error(body.message || body.error || `Catalogo non disponibile (${res.status})`);
        if (!cancelled) {
          setPlan(Array.isArray(body.plans) && body.plans.length ? body.plans[0] : null);
        }
      } catch (err) {
        if (!cancelled) {
          setPlan(null);
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCategory]);

  const modules = (plan?.modules || []).sort((a, b) => (Number(a.price_monthly) || 0) - (Number(b.price_monthly) || 0));

  return (
    <div className="sk-join">
      <div className="sk-join__glow" aria-hidden="true" />

      <header className="sk-join__nav">
        <Link to="/" className="sk-join__nav-brand" aria-label="StrikerPos">
          <StrikerBrand surface="dark" title="StrikerPos" />
        </Link>
        <div className="sk-join__nav-actions">
          <Link className="sk-join__nav-link" to="/locali">
            Locali
          </Link>
          <Link className="sk-join__nav-cta" to="/login">
            Accedi
          </Link>
        </div>
      </header>

      <main className="sk-join__main">
        {/* === Hero === */}
        <section className="sk-join__hero">
          <p className="sk-join__eyebrow">Iscriviti</p>
          <h1 className="sk-join__title">Unisciti a StrikerPos</h1>
          <p className="sk-join__lead">
            Che tu sia un locale o un cliente, qui trovi tutto per iniziare.
            Iscrizione gratuita — scegli i moduli che ti servono.
          </p>
        </section>

        {/* === Sezione: Per Locali === */}
        <section className="sk-join__section" id="locali">
          <div className="sk-join__section-head">
            <p className="sk-join__eyebrow">Per locali</p>
            <h2 className="sk-join__section-title">Apri il tuo locale su StrikerPos</h2>
            <p className="sk-join__section-lead">
              Scegli la categoria, inserisci i dati e conferma la carta su Stripe.
              Iscrizione gratuita — acquisti i moduli dallo store quando vuoi.
            </p>
          </div>

          {/* Category selector */}
          {categories.length > 0 ? (
            <div className="sk-join__cat-bar">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`sk-join__cat-chip${selectedCategory === cat.code ? ' sk-join__cat-chip--active' : ''}`}
                  onClick={() => setSelectedCategory(cat.code)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          ) : null}

          {busy ? <p className="sk-join__status">Caricamento moduli…</p> : null}
          {loadError ? <p className="sk-join__status sk-join__status--err">{loadError}</p> : null}

          {!busy && !loadError && modules.length === 0 ? (
            <p className="sk-join__status">Nessun modulo pubblicato al momento.</p>
          ) : null}

          <div className="sk-join__plans">
            {modules.map((mod) => (
              <article key={mod.code} className="sk-join__plan">
                <h3 className="sk-join__plan-name">{mod.name}</h3>
                <p className="sk-join__plan-price">
                  <span className="sk-join__plan-amount">{formatPrice(Number(mod.price_monthly) || 0, plan?.currency_symbol || '€')}</span>
                  <span className="sk-join__plan-period"> / mese</span>
                </p>
                <p className="sk-join__plan-trial">Modulo a acquisto</p>
                <Link
                  className="sk-btn sk-btn--primary sk-join__plan-cta"
                  to={`/register${selectedCategory ? `?category=${selectedCategory}` : ''}`}
                >
                  Registrati
                </Link>
              </article>
            ))}
          </div>

          <p className="sk-join__hint">
            Hai già un account? <Link to="/login">Accedi</Link>
          </p>
        </section>

        {/* === Sezione: Per Clienti === */}
        <section className="sk-join__section sk-join__section--alt" id="clienti">
          <div className="sk-join__section-head">
            <p className="sk-join__eyebrow">Per clienti</p>
            <h2 className="sk-join__section-title">Passaporto StrikerPos</h2>
            <p className="sk-join__section-lead">
              Crea il tuo profilo, accumula fiches di piattaforma, sblocca badge
              e recensioni verificate nei locali partner.
            </p>
          </div>

          <div className="sk-join__enduser">
            <div className="sk-join__enduser-card">
              <h3 className="sk-join__enduser-title">Cosa ottieni</h3>
              <ul className="sk-join__enduser-list">
                <li>Reputazione numerica trasparente</li>
                <li>Fiches di Piattaforma spendibili come sconto</li>
                <li>Badge e traguardi (Passaporto StrikerPos)</li>
                <li>Recensioni verificate post-visita</li>
              </ul>
              <Link
                className="sk-btn sk-btn--primary"
                to="/end-user/register"
              >
                Crea il tuo Passaporto
              </Link>
            </div>
            <div className="sk-join__enduser-card sk-join__enduser-card--placeholder">
              <p className="sk-join__placeholder-label">Info extra</p>
              <p className="sk-join__placeholder-text">
                Qui inseriremo promozioni, programmi fedeltà e dettagli sui benefit.
              </p>
            </div>
          </div>
        </section>

        {/* === Sezione: App Mobile === */}
        <section className="sk-join__section" id="app">
          <div className="sk-join__section-head">
            <p className="sk-join__eyebrow">App Mobile</p>
            <h2 className="sk-join__section-title">Staff Sala per Android</h2>
            <p className="sk-join__section-lead">
              L'app per la sala: comande, sinottica e stato tavoli dal palmare.
            </p>
          </div>

          <div className="sk-join__app">
            <a href="/downloads/" className="sk-btn sk-btn--primary sk-join__app-btn">
              <svg
                className="sk-join__app-icon"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
                aria-hidden
              >
                <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48A10.81 10.81 0 0 0 1 18h22a10.81 10.81 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"/>
              </svg>
              <span>Scarica APK</span>
            </a>
            <div className="sk-join__app-card sk-join__app-card--placeholder">
              <p className="sk-join__placeholder-label">Info extra</p>
              <p className="sk-join__placeholder-text">
                Qui inseriremo changelog, requisiti dispositivo e guida installazione.
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="sk-join__footer">
        <StrikerBrand surface="dark" title="StrikerPos" />
        <p className="sk-join__footer-copy">Un turno, un sistema.</p>
        <div className="sk-join__footer-links">
          <Link className="sk-btn sk-btn--ghost" to="/">
            Home
          </Link>
          <Link className="sk-btn sk-btn--ghost" to="/login">
            Accedi
          </Link>
          <Link className="sk-btn sk-btn--ghost" to="/locali">
            Trova un locale
          </Link>
        </div>
      </footer>
    </div>
  );
}
